/**
 * Frontend entry point.
 *
 * Routing: History API, clean URLs — /login, /register, /forgot-password,
 * /reset-password?token=..., /verify-email?token=..., /play (authenticated).
 */
import { bootstrapSession, logout } from './core/auth/auth-api';
import { renderLogin } from './core/auth/login-screen';
import { renderRegister } from './core/auth/register-screen';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { authStore, AuthUser } from './core/state/auth-store';
import { connectSocket, disconnectSocket } from './core/network/socket';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
import { mountDungeonPreview } from './engine/dungeon-preview-bootstrap';
import { mountDungeonInstance, unmountDungeonInstance } from './engine/dungeon-instance-bootstrap';
import { mountSession } from './engine/session-bootstrap';
import { mountAdminPanel } from './engine/admin-panel/admin-panel-bootstrap';
import { mountBackToLanding } from './engine/ui/back-to-landing';
import { mountLegalPage, slugFromPath } from './engine/ui/legal-page';
import { mountNewsPage, newsSlugFromPath } from './engine/ui/news-page';
import { requireManualAltcha } from './engine/ui/altcha-manual';
import { createSessionRecovery } from './engine/session-recovery';
import { initI18n } from './engine/i18n/i18n';
import { openSettings } from './engine/ui/settings/settings-modal';

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;

/**
 * `/play` mounts the Sconce Hub by default (design/gdd/sconce-hub.md) — the
 * real production path into a match is queueing from there, not a direct
 * join. `?view=dungeon` mounts the procedural-generation layout viewer.
 * `?view=dungeon_instance` is a dev/QA bypass straight into a match,
 * skipping Matchmaking entirely — kept for the same direct-join testing
 * this route always supported, now that the bare route goes through the hub.
 */
function mountByView(view: string | null, root: HTMLElement, user: AuthUser): () => void {
  const onLogout = () => { logout(); disconnectSocket(); navigate('/login'); };
  // Dev builds only (QA critic defect A, 2026-10-16): a production player has
  // no business bypassing the queue. The server refuses an unticketed join to
  // a matchmade room in any build; this just keeps the door out of prod.
  if (import.meta.env.DEV && view === 'dungeon') return mountDungeonPreview(root, user, onLogout);
  if (import.meta.env.DEV && view === 'dungeon_instance') return mountDungeonInstance(root, user, onLogout);
  return mountSession(root, user, onLogout);
}

/** Core auth screen teardown + the game's captcha override (engine/ui/altcha-manual.ts). */
function withManualAltcha(unmountScreen: () => void): () => void {
  const unmountCaptcha = requireManualAltcha(app);
  return () => { unmountCaptcha(); unmountScreen(); };
}

export function navigate(to: string) {
  if (location.pathname !== to) history.pushState(null, '', to);
  render();
}

function render() {
  teardown?.();
  teardown = null;

  const path = location.pathname;
  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  // Public CMS legal pages, linked from the landing footer — no sign-in.
  const legalSlug = slugFromPath(path);
  if (legalSlug) {
    teardown = mountLegalPage(app, legalSlug);
    return;
  }
  // Public CMS news articles, linked from the landing's News section.
  const newsSlug = newsSlugFromPath(path);
  if (newsSlug) {
    teardown = mountNewsPage(app, newsSlug);
    return;
  }

  const afterAuth = () => {
    const u = authStore.get().user;
    if (u && !u.emailVerified) { navigate('/check-email'); return; }
    connectSocket();
    navigate('/play');
  };

  switch (path) {
    case '/login':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      {
        const unmountLogin = renderLogin(app, afterAuth);
        const unmountCaptcha = requireManualAltcha(app);
        const unmountBack = mountBackToLanding(app);
        teardown = () => { unmountBack(); unmountCaptcha(); unmountLogin(); };
      }
      break;
    case '/register':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = withManualAltcha(renderRegister(app, afterAuth));
      break;
    case '/forgot-password':
      if (user) { navigate('/play'); return; }
      teardown = withManualAltcha(renderForgotPassword(app));
      break;
    case '/reset-password':
      teardown = withManualAltcha(renderResetPassword(app, params.get('token') ?? '', () => navigate('/login')));
      break;
    case '/verify-email':
      teardown = renderVerifyEmail(app, params.get('token') ?? '');
      break;
    case '/check-email':
      if (!user) { navigate('/login'); return; }
      teardown = renderCheckEmail(app, user.email, () => { disconnectSocket(); navigate('/login'); });
      break;
    case '/admin-panel':
      if (!user) { navigate('/login'); return; }
      if (user.role !== 'ADMIN') { navigate('/play'); return; }
      teardown = mountAdminPanel(app, user, () => navigate('/play'));
      break;
    case '/play':
    case '/':
      if (!user) { navigate('/login'); return; }
      if (!user.emailVerified) { navigate('/check-email'); return; }
      connectSocket();
      teardown = mountByView(params.get('view'), app, user);
      break;
    default:
      if (path.startsWith('/settings')) {
        if (!user) { navigate('/login'); return; }
        if (!user.emailVerified) { navigate('/check-email'); return; }
        connectSocket();
        const section = path.split('/')[2] || 'profile';
        teardown = mountByView(null, app, user);
        openSettings(section as any);
        break;
      }
      navigate('/login');
  }
}

window.addEventListener('popstate', render);

document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target || a.origin !== location.origin) return;
  e.preventDefault();
  navigate(a.pathname + a.search);
});

(async () => {
  // Fetch site-info first — drives preview-mode gating throughout the app
  // The catalogue loads alongside site-info, before anything is drawn: a
  // screen rendered first would stay English until its next re-render.
  await Promise.all([fetchSiteInfo(), initI18n()]);
  if (isPreviewBuild()) stripBrandMeta();

  await bootstrapSession();
  render();

  // A user dropped on /play may only have a lapsed access cookie — try the
  // refresh cookie once (at most every 30s) before "Welcome back".
  // See engine/session-recovery.ts (QA 10.10 #13).
  const recoverSession = createSessionRecovery({
    refresh: bootstrapSession,
    onRecovered: connectSocket,
    onGiveUp: () => { if (location.pathname === '/play') navigate('/login'); },
  });
  authStore.subscribe((s) => {
    if (!s.user && location.pathname === '/play') recoverSession();
  });
})();

window.addEventListener('beforeunload', () => {
  teardown?.();
  unmountDungeonInstance();
});
